import React, { useState } from "react";
import { Opportunity, RequiredForm } from "../../types";
import { useStaffDirectory } from "../../services/staffService";
import { attachFormFile, detachFormFile } from "../../services/requirementsService";
import { getDocumentDownloadUrl } from "../../services/storageService";
import { Modal } from "../common/Modal";
import {
  FileText,
  Plus,
  Paperclip,
  Download,
  Trash2,
  Edit2,
  CheckCircle2,
  AlertCircle,
  Clock,
  UserCheck,
  Loader2,
  FileCheck,
} from "lucide-react";

interface FormsChecklistProps {
  opportunity: Opportunity;
  forms: RequiredForm[];
  onAddForm: () => void;
  onEditForm: (form: RequiredForm) => void;
  onDeleteForm: (formId: string) => Promise<void>;
  onToggleConfirmed: (form: RequiredForm) => void;
  onUpdateStatus: (formId: string, status: RequiredForm["status"]) => void;
  onAssignStaff: (formId: string, staff: { displayName: string; email: string } | null) => void;
  onUpdateNotes: (formId: string, notes: string) => void;
}

export const FormsChecklist: React.FC<FormsChecklistProps> = ({
  opportunity,
  forms,
  onAddForm,
  onEditForm,
  onDeleteForm,
  onToggleConfirmed,
  onUpdateStatus,
  onAssignStaff,
  onUpdateNotes,
}) => {
  const { staff } = useStaffDirectory({ rfpOnly: true });
  const [uploadingFormId, setUploadingFormId] = useState<string | null>(null);
  const [downloadingFormId, setDownloadingFormId] = useState<string | null>(null);
  const [formToDelete, setFormToDelete] = useState<RequiredForm | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // KPI counts
  const total = forms.length;
  const completedCount = forms.filter(
    (f) => f.status === "attached" || f.status === "signed",
  ).length;
  const inProgressCount = forms.filter((f) => f.status === "in_progress").length;
  const waivedCount = forms.filter((f) => f.status === "waived").length;

  const handleFileUpload = async (form: RequiredForm, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingFormId(form.id);
    try {
      await attachFormFile(opportunity.id, form.id, file);
    } catch (err) {
      console.error("Failed to attach form file:", err);
    } finally {
      setUploadingFormId(null);
      e.target.value = "";
    }
  };

  const handleDetachFile = async (form: RequiredForm) => {
    try {
      await detachFormFile(opportunity.id, form.id, form.storagePath);
    } catch (err) {
      console.error("Failed to detach form file:", err);
    }
  };

  const handleDownloadFile = async (form: RequiredForm) => {
    if (!form.storagePath) return;
    setDownloadingFormId(form.id);
    try {
      const url = await getDocumentDownloadUrl(form.storagePath);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Failed to download form:", err);
    } finally {
      setDownloadingFormId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!formToDelete) return;
    setIsDeleting(true);
    try {
      await onDeleteForm(formToDelete.id);
      setFormToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* KPI Header */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="glass rounded-xl border border-slate-200/80 p-3 shadow-xs dark:border-slate-800">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-500 uppercase">
            <span>Required Forms</span>
            <FileText className="h-4 w-4 text-[#2ba5b5]" />
          </div>
          <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">{total}</p>
        </div>

        <div className="glass rounded-xl border border-slate-200/80 p-3 shadow-xs dark:border-slate-800">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-500 uppercase">
            <span>Completed / Signed</span>
            <FileCheck className="h-4 w-4 text-emerald-500" />
          </div>
          <p className="mt-1 text-2xl font-bold text-emerald-600 dark:text-emerald-400">
            {completedCount}
          </p>
        </div>

        <div className="glass rounded-xl border border-slate-200/80 p-3 shadow-xs dark:border-slate-800">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-500 uppercase">
            <span>In Progress</span>
            <Clock className="h-4 w-4 text-blue-500" />
          </div>
          <p className="mt-1 text-2xl font-bold text-blue-600 dark:text-blue-400">
            {inProgressCount}
          </p>
        </div>

        <div className="glass rounded-xl border border-slate-200/80 p-3 shadow-xs dark:border-slate-800">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-500 uppercase">
            <span>Waived / Exempt</span>
            <AlertCircle className="h-4 w-4 text-slate-400" />
          </div>
          <p className="mt-1 text-2xl font-bold text-slate-600 dark:text-slate-400">
            {waivedCount}
          </p>
        </div>
      </div>

      {/* Header with Add Button */}
      <div className="flex items-center justify-between pt-1">
        <div>
          <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
            RFP Required Forms Checklist
          </h3>
          <p className="text-xs text-slate-500">
            Track mandatory submission forms, certificates, signatures, and executed documents.
          </p>
        </div>
        <button
          type="button"
          onClick={onAddForm}
          className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-3 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-[#238997]"
        >
          <Plus className="h-3.5 w-3.5" />
          <span>Add Form</span>
        </button>
      </div>

      {/* Forms List */}
      {forms.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center dark:border-slate-700">
          <FileText className="mx-auto h-8 w-8 text-slate-400" />
          <h4 className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
            No Required Forms Found
          </h4>
          <p className="mt-1 text-xs text-slate-500">
            Extract forms from the RFP source document or add them manually.
          </p>
          <button
            type="button"
            onClick={onAddForm}
            className="btn-animate mt-3 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <Plus className="h-3.5 w-3.5" />
            <span>Add Form</span>
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {forms.map((form) => (
            <div
              key={form.id}
              className={`glass rounded-xl border p-4 transition-all shadow-xs ${
                form.confirmed
                  ? "border-emerald-200/80 bg-emerald-50/20 dark:border-emerald-900/40 dark:bg-emerald-950/10"
                  : "border-slate-200/80 bg-white hover:border-slate-300 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700"
              }`}
            >
              {/* Top Row: Title, Badges & Actions */}
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`rounded-md px-2 py-0.5 text-[11px] font-bold ${
                      form.mandatory
                        ? "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400"
                        : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
                    }`}
                  >
                    {form.mandatory ? "Mandatory" : "Optional"}
                  </span>

                  <h4 className="text-xs font-bold text-slate-900 dark:text-slate-100">
                    {form.formName}
                  </h4>

                  {form.sourcePage !== undefined && (
                    <span className="text-[10px] text-slate-400">Page {form.sourcePage}</span>
                  )}

                  {form.addendumTags && form.addendumTags.length > 0 && (
                    <div className="flex items-center gap-1">
                      {form.addendumTags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded bg-[#2ba5b5]/15 px-1.5 py-0.5 text-[10px] font-bold text-[#1b7582] dark:bg-[#2ba5b5]/25 dark:text-[#52c8d7]"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Right Actions: Confirm, Edit, Delete */}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onToggleConfirmed(form)}
                    className={`btn-animate inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold ${
                      form.confirmed
                        ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300"
                        : "border border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                    }`}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    <span>{form.confirmed ? "Confirmed" : "Confirm"}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => onEditForm(form)}
                    className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                    title="Edit form"
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                  </button>

                  <button
                    type="button"
                    onClick={() => setFormToDelete(form)}
                    className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-rose-600 dark:hover:bg-slate-800 dark:hover:text-rose-400"
                    title="Delete form"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              {/* Description */}
              {form.description && (
                <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                  {form.description}
                </p>
              )}

              {/* Middle Row: Assignee, Status, Attachment */}
              <div className="mt-3 flex flex-wrap items-center gap-3 pt-2.5 border-t border-slate-100 dark:border-slate-800/80">
                {/* Status Dropdown */}
                <div className="flex items-center gap-1.5 text-xs">
                  <span className="text-[11px] font-semibold text-slate-400">Status:</span>
                  <select
                    value={form.status}
                    onChange={(e) =>
                      onUpdateStatus(form.id, e.target.value as RequiredForm["status"])
                    }
                    className={`rounded-lg border px-2 py-1 text-xs font-semibold ${
                      form.status === "signed" || form.status === "attached"
                        ? "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
                        : form.status === "in_progress"
                          ? "border-blue-300 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300"
                          : form.status === "waived"
                            ? "border-slate-300 bg-slate-100 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
                            : "border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                    }`}
                  >
                    <option value="not_started">Not Started</option>
                    <option value="in_progress">In Progress</option>
                    <option value="attached">Attached</option>
                    <option value="signed">Signed</option>
                    <option value="waived">Waived / Exempt</option>
                  </select>
                </div>

                {/* Assignee Dropdown */}
                <div className="flex items-center gap-1.5 text-xs">
                  <UserCheck className="h-3.5 w-3.5 text-slate-400" />
                  <span className="text-[11px] font-semibold text-slate-400">Owner:</span>
                  <select
                    value={form.assignedToEmail || ""}
                    onChange={(e) => {
                      const email = e.target.value;
                      if (!email) {
                        onAssignStaff(form.id, null);
                      } else {
                        const found = staff.find((s) => s.email === email);
                        if (found) {
                          onAssignStaff(form.id, {
                            displayName: found.displayName,
                            email: found.email,
                          });
                        }
                      }
                    }}
                    className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  >
                    <option value="">Unassigned</option>
                    {staff.map((s) => (
                      <option key={s.email} value={s.email}>
                        {s.displayName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* File Attachment / Download */}
                <div className="flex items-center gap-2">
                  {form.storagePath ? (
                    <div className="flex items-center gap-1.5 rounded-lg bg-slate-50 px-2 py-1 text-xs border border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                      <Paperclip className="h-3.5 w-3.5 text-[#2ba5b5]" />
                      <span className="max-w-[150px] truncate text-[11px] font-medium text-slate-700 dark:text-slate-300">
                        {form.fileName || "Attached Document"}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleDownloadFile(form)}
                        disabled={downloadingFormId === form.id}
                        className="ml-1 text-[#2ba5b5] hover:text-[#238997] dark:text-[#52c8d7]"
                        title="Download form"
                      >
                        {downloadingFormId === form.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Download className="h-3.5 w-3.5" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDetachFile(form)}
                        className="text-slate-400 hover:text-rose-500"
                        title="Detach document"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="btn-animate inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-600 hover:border-[#2ba5b5] hover:text-[#2ba5b5] dark:border-slate-700 dark:text-slate-400">
                      {uploadingFormId === form.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-[#2ba5b5]" />
                      ) : (
                        <Paperclip className="h-3.5 w-3.5" />
                      )}
                      <span>Attach Document</span>
                      <input
                        type="file"
                        className="hidden"
                        onChange={(e) => handleFileUpload(form, e)}
                        disabled={uploadingFormId === form.id}
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Bottom Row: Notes input */}
              <div className="mt-2 pt-1.5">
                <input
                  type="text"
                  placeholder="Add preparation / notarization notes..."
                  defaultValue={form.notes || ""}
                  onBlur={(e) => {
                    if (e.target.value !== (form.notes || "")) {
                      onUpdateNotes(form.id, e.target.value);
                    }
                  }}
                  className="w-full rounded-md border border-transparent px-2 py-1 text-[11px] text-slate-700 hover:border-slate-200 focus:border-[#2ba5b5] focus:bg-white focus:outline-hidden dark:text-slate-300 dark:hover:border-slate-700 dark:focus:bg-slate-950"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Form Confirmation Modal */}
      <Modal
        isOpen={Boolean(formToDelete)}
        onClose={() => setFormToDelete(null)}
        title="Confirm Form Deletion"
      >
        <div className="space-y-4">
          <p className="text-xs text-slate-600 dark:text-slate-300">
            Are you sure you want to delete{" "}
            <strong className="text-slate-900 dark:text-slate-100">{formToDelete?.formName}</strong>
            ? Any attached files in storage will need to be re-uploaded if recreated.
          </p>
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-800">
            <button
              type="button"
              onClick={() => setFormToDelete(null)}
              className="rounded-lg border border-slate-200 px-3.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={isDeleting}
              onClick={handleConfirmDelete}
              className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-rose-700 disabled:opacity-50"
            >
              {isDeleting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              <span>Delete Form</span>
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
